// ============================================================================
// THE ICON SET — hand-written inline SVG, transcribed path-for-path from the
// mockups in docs/design/handoff/.
//
// WHY NOT AN ICON LIBRARY. The handoff's icon spec is exact: viewBox 0 0 24 24,
// fill none, stroke = a domain or status hue, stroke-width 1.6–2.4 (1.7–1.8
// typical), round caps and joins. A library's icons are drawn on a different
// grid at a different optical weight, so matching the mockups would mean
// overriding every one of them anyway. There are nine glyphs on this screen.
//
// COLOUR COMES FROM THE TOKEN, NEVER FROM A PROP. Every path is
// stroke="currentColor"; the caller sets `text-school`, `text-danger-dot`, and
// so on. That is what keeps scripts/ship-gate.sh's brand audit satisfiable —
// a raw hex in here would fail the build.
//
// Provenance of each path is named on the glyph. `ChatBubble` is the one glyph
// with no mockup source: Chat is an 8th rail item added by the orchestrator
// decision recorded in docs/design/FIDELITY.md, and the handoff's rail predates
// it. It is drawn in the same idiom and should be replaced the day a mockup
// carries one.
// ============================================================================

export interface IconProps {
  /** Rendered box in px. The handoff uses 10–22; 18 is the rail size. */
  size?: number
  /** 1.6–2.4 per the handoff; 1.7 is the rail, 1.8 the card headers. */
  strokeWidth?: number
  className?: string
}

function Glyph({
  size = 18,
  strokeWidth = 1.7,
  className,
  d,
}: IconProps & { d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  )
}

// ── Rail glyphs (LifeOS Home Options.dc.html, LifeOS Connections.dc.html) ────

/** Today. */
export const Sun = (p: IconProps) => (
  <Glyph
    {...p}
    d="M12 6.8a5.2 5.2 0 1 1 0 10.4 5.2 5.2 0 0 1 0-10.4M12 2v2.2M12 19.8V22M2 12h2.2M19.8 12H22M5.1 5.1l1.6 1.6M17.3 17.3l1.6 1.6M18.9 5.1l-1.6 1.6M6.7 17.3l-1.6 1.6"
  />
)

/** Journal. */
export const Book = (p: IconProps) => (
  <Glyph
    {...p}
    d="M5 4.6A1.6 1.6 0 0 1 6.6 3H18.4a.6.6 0 0 1 .6.6v16.8a.6.6 0 0 1-.6.6H6.6A1.6 1.6 0 0 1 5 19.4zM5 17.6h14M8.6 7.4h6.4M8.6 11h6.4"
  />
)

/**
 * A generic date/calendar glyph — NOT a domain marker. It used to be School's rail icon; the
 * owner asked School to wear the graduation cap instead (2026-08-29, "too similar in look and
 * color" to the Calendar rail item), so this shape is now only the "pick a date" affordance
 * (Reminders' due-date button, the tasks Upcoming lens) with no hue of its own.
 */
export const Calendar = (p: IconProps) => (
  <Glyph
    {...p}
    d="M4 7.4A2.4 2.4 0 0 1 6.4 5h11.2A2.4 2.4 0 0 1 20 7.4v11.2A2.4 2.4 0 0 1 17.6 21H6.4A2.4 2.4 0 0 1 4 18.6zM4 10h16M8.6 3v3.6M15.4 3v3.6"
  />
)

/**
 * Calendar — the rail item, and the ONE glyph on this screen with no mockup behind it.
 *
 * docs/design/calendar-brief.md says a calendar icon already exists in the 62px rail, and names
 * its stroke. In the shipped rail that calendar FRAME and that slot belong to School (`/school`),
 * because the handoff's own rail put it there — so the Calendar page gets an item of its own,
 * drawn as the week GRID it opens onto rather than as a second calendar frame nobody could tell
 * from the first. The brief's stroke is kept exactly, as `--color-calendar` in index.css.
 * Reported to the orchestrator as a brief-vs-shipped-rail conflict; replace it the day a
 * mockup carries one.
 */
export const CalendarGrid = (p: IconProps) => (
  <Glyph
    {...p}
    d="M4 7.4A2.4 2.4 0 0 1 6.4 5h11.2A2.4 2.4 0 0 1 20 7.4v11.2A2.4 2.4 0 0 1 17.6 21H6.4A2.4 2.4 0 0 1 4 18.6zM4 10h16M9.6 10v11M14.4 10v11"
  />
)

/** Tasks. */
export const CheckSquare = (p: IconProps) => (
  <Glyph
    {...p}
    d="M4 6.6A2.6 2.6 0 0 1 6.6 4h10.8A2.6 2.6 0 0 1 20 6.6v10.8A2.6 2.6 0 0 1 17.4 20H6.6A2.6 2.6 0 0 1 4 17.4zM8.4 12.2l2.6 2.6 4.8-5"
  />
)

/**
 * School — the classic mortarboard (owner reference, 2026-08-29). It used to be Learning's; the
 * two nav items read "too similar in look and color" as a calendar-frame pair, so School gets the
 * cap it explicitly asked for and Learning moves to `OpenBook` below.
 */
export const GraduationCap = (p: IconProps) => (
  <Glyph {...p} d="M3 9.6 12 5.2l9 4.4-9 4.4zM7 11.9v4.4c0 1.3 2.2 2.3 5 2.3s5-1 5-2.3v-4.4M20 10.6v4.6" />
)

/**
 * Learning — an open book, taking over from `GraduationCap` (see above). Drawn as two fanned
 * pages rather than `Book`'s closed-cover rectangle (Journal's glyph): the two must read apart at
 * 18px, and "open" vs "closed" is the distinction the two screens actually have.
 */
export const OpenBook = (p: IconProps) => (
  <Glyph {...p} d="M3 5.4 11.2 6.6V18.6L3 17.4zM21 5.4 12.8 6.6V18.6L21 17.4z" />
)

/** Interests. */
export const Star = (p: IconProps) => (
  <Glyph {...p} d="M12 3.6l2.6 5.4 5.9.9-4.3 4.1 1 5.9-5.2-2.8-5.2 2.8 1-5.9L3.5 9.9l5.9-.9z" />
)

/**
 * Chat. NO MOCKUP SOURCE — see the file header. Drawn to the same spec
 * (24 grid, round joins, the mockups' corner radius idiom).
 */
export const ChatBubble = (p: IconProps) => (
  <Glyph
    {...p}
    d="M4.6 7A2.4 2.4 0 0 1 7 4.6h10A2.4 2.4 0 0 1 19.4 7v6.8a2.4 2.4 0 0 1-2.4 2.4H9.6l-4 3.2v-3.2h-.6a.4.4 0 0 1-.4-.4z"
  />
)

/** Settings — the one rail item that is not a domain hue. */
export const Gear = (p: IconProps) => (
  <Glyph
    {...p}
    d="M12 9.2a2.8 2.8 0 1 1 0 5.6 2.8 2.8 0 0 1 0-5.6M12 3.4l1 2.2 2.4-.5 1.4 2.4-1.7 1.8 1.7 1.8-1.4 2.4-2.4-.5-1 2.2h-.1l-1-2.2-2.4.5L7.1 13l1.7-1.8L7.1 9.4l1.4-2.3 2.4.5 1-2.2z"
  />
)

/**
 * Dark theme. NO MOCKUP SOURCE — the handoff designs one appearance, and dark
 * mode is an owner request on top of it (2026-08-26). Drawn to the same spec as
 * `Sun` directly above so the two read as a pair in the Appearance control.
 */
export const Moon = (p: IconProps) => (
  <Glyph {...p} d="M20 14.4A8.6 8.6 0 0 1 9.6 4 8.4 8.4 0 1 0 20 14.4" />
)

// ── Screen glyphs (LifeOS Home Options.dc.html, option 3c) ──────────────────

/** The header search field. Search is not a rail item — it rides this field. */
export const Search = ({ size = 15, strokeWidth = 1.8, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M10.6 4a6.6 6.6 0 1 1 0 13.2 6.6 6.6 0 0 1 0-13.2M15.3 15.3 20 20" />
  </svg>
)

/** DAILY PROMPT eyebrow. */
export const Pencil = (p: IconProps) => (
  <Glyph {...p} d="M4.5 19.5h3.8L20 7.8a2.7 2.7 0 0 0-3.8-3.8L4.5 15.7zM14.8 5.4l3.8 3.8" />
)

/** The "why this prompt" glyph. */
export const Info = (p: IconProps) => (
  <Glyph {...p} d="M12 3.6a8.4 8.4 0 1 1 0 16.8 8.4 8.4 0 0 1 0-16.8M12 11v5.4M12 7.7v.2" />
)

/** DUE SOON. */
export const Clock = (p: IconProps) => <Glyph {...p} d="M12 4a8 8 0 1 1 0 16 8 8 0 0 1 0-16M12 8.4V12l2.6 2.2" />

/** Reminders. */
export const Bell = (p: IconProps) => (
  <Glyph {...p} d="M9.4 18.4a2.7 2.7 0 0 0 5.2 0M6.2 18.4h11.6l-1.6-2.4v-5a4.2 4.2 0 0 0-8.4 0v5z" />
)

/** A transcribed voice memo. Drawn at 2.4 in the mockup — a heavier tick. */
export const Check = (p: IconProps) => <Glyph strokeWidth={2.4} {...p} d="M5 12.6l4.4 4.4L19 7.4" />

/** Cancel / discard. No mockup source — see `ChatBubble` above for the convention this follows. */
export const X = (p: IconProps) => <Glyph {...p} d="M6 6l12 12M18 6L6 18" />
